import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { toFarsiNumber } from "../../../custom/helpers";
import { formatJalali } from "../../challenges/jalaliDate";
import { useGroupApi, inviteLink } from "../groupApi";
import { Sheet } from "./ui";

const EXPIRY = [
  { label: "۱ روز", value: 1 },
  { label: "۷ روز", value: 7 },
  { label: "۳۰ روز", value: 30 },
  { label: "بدون انقضا", value: null },
];
const USES = [
  { label: "چندبار مصرف", value: null },
  { label: "یک‌بار مصرف", value: 1 },
];
const STATUS = { active: "فعال", revoked: "باطل‌شده", expired: "منقضی", used: "استفاده‌شده" };

export default function InviteModal({ group, onClose }) {
  const { call, json } = useGroupApi();
  const [expires, setExpires] = useState(7);
  const [maxUses, setMaxUses] = useState(null);
  const [link, setLink] = useState(null);
  const [busy, setBusy] = useState(false);
  const [invites, setInvites] = useState(null);

  const loadInvites = useCallback(async () => {
    try {
      const r = await call(`groups/${group.group_id}/invitations`, { method: "GET" });
      setInvites(r.invitations);
    } catch (e) {
      setInvites([]);
    }
  }, [call, group.group_id]);

  useEffect(() => { loadInvites(); }, [loadInvites]);

  const create = async () => {
    setBusy(true);
    try {
      const r = await json(`groups/${group.group_id}/invitations`, "POST", { expires_in_days: expires, max_uses: maxUses });
      setLink(inviteLink(r.token));
      loadInvites();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (id) => {
    try {
      await call(`groups/${group.group_id}/invitations/${id}`, { method: "DELETE" });
      toast.success("لینک باطل شد");
      if (link) setLink(null); // the link on screen may be the one just revoked
      loadInvites();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      toast.success("لینک کپی شد");
    } catch (e) {
      toast.info("لینک رو دستی کپی کن");
    }
  };

  const text = `به گروه «${group.name}» در فودپلنر بپیوند و با هم برنامه غذایی بچینیم:`;
  const enc = encodeURIComponent;
  const canShare = typeof navigator !== "undefined" && !!navigator.share;
  const active = (invites || []).filter((i) => i.status === "active");

  return (
    <Sheet title="دعوت به گروه" onClose={onClose}>
      {!link ? (
        <>
          <div className="grpSectionTitle yekan">اعتبار لینک</div>
          <div className="grpChoice">
            {EXPIRY.map((o) => (
              <button key={o.label} type="button" className={`grpChip ${expires === o.value ? "grpChip-active" : ""}`} onClick={() => setExpires(o.value)}>{o.label}</button>
            ))}
          </div>
          <div className="grpChoice">
            {USES.map((o) => (
              <button key={o.label} type="button" className={`grpChip ${maxUses === o.value ? "grpChip-active" : ""}`} onClick={() => setMaxUses(o.value)}>{o.label}</button>
            ))}
          </div>
          <button type="button" className="grpBtn grpBtn-block" disabled={busy} onClick={create}>
            {busy ? "..." : "ساخت لینک دعوت"}
          </button>
        </>
      ) : (
        <>
          <div className="grpLinkBox">{link}</div>
          <div className="grpShareGrid">
            <button type="button" className="grpBtn" onClick={copy}>کپی لینک</button>
            {canShare && (
              <button type="button" className="grpBtn grpBtn-light" onClick={() => navigator.share({ title: group.name, text, url: link }).catch(() => {})}>اشتراک‌گذاری</button>
            )}
            <a className="grpBtn grpBtn-light" target="_blank" rel="noreferrer" href={`https://wa.me/?text=${enc(`${text}\n${link}`)}`}>واتساپ</a>
            <a className="grpBtn grpBtn-light" target="_blank" rel="noreferrer" href={`https://t.me/share/url?url=${enc(link)}&text=${enc(text)}`}>تلگرام</a>
            <a className="grpBtn grpBtn-light" href={`sms:?&body=${enc(`${text}\n${link}`)}`}>پیامک</a>
          </div>
          <p className="grey-2 caption-1 yekan">هرکسی این لینک رو داشته باشه می‌تونه به گروه بپیونده. اگه جایی اشتباهی پخش شد، لینک رو باطل کن.</p>
          <button type="button" className="grpBtn grpBtn-ghost grpBtn-sm" onClick={() => setLink(null)}>ساخت لینک جدید</button>
        </>
      )}

      <div className="grpSection">
        <div className="grpSectionTitle yekan">لینک‌های فعال ({toFarsiNumber(active.length)})</div>
        {invites === null && <div className="grpSkeleton" style={{ height: 44 }} />}
        {invites && active.length === 0 && <p className="grey-2 caption-1 yekan">لینک فعالی وجود نداره.</p>}
        {active.map((i) => (
          <div className="grpRow" key={i.invitation_id}>
            <div className="yekan caption-1">
              {toFarsiNumber(formatJalali(i.created_at, "DD MMMM"))}
              {" · "}
              {i.expires_at ? `تا ${toFarsiNumber(formatJalali(i.expires_at, "DD MMMM"))}` : "بدون انقضا"}
              {" · "}
              {i.max_uses ? `${toFarsiNumber(i.use_count)}/${toFarsiNumber(i.max_uses)} استفاده` : `${toFarsiNumber(i.use_count)} استفاده`}
              <span className="grpBadge grpBadge-edit">{STATUS[i.status]}</span>
            </div>
            <button type="button" className="grpBtn grpBtn-danger grpBtn-sm" onClick={() => revoke(i.invitation_id)}>باطل کن</button>
          </div>
        ))}
      </div>
    </Sheet>
  );
}
